'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import { Menu, X } from 'lucide-react';

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Availability', href: '#availability' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Book Now', href: '#booking' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300" suppressHydrationWarning>
      <div
        className={
          scrolled
            ? 'bg-card/95 backdrop-blur-md border-b neon-border-gold' :'bg-transparent'
        }
        suppressHydrationWarning
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <img
              src="/assets/images/760105518_1676653893415634_6565105660036259608_n-1-1785527968606.webp"
              alt="DJ ExXtra Maximum Effort logo"
              width={44}
              height={44}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                objectFit: 'cover',
                display: 'block',
                flexShrink: 0,
              }}
            />
            <div className="flex flex-col leading-none">
              <span className="text-xs font-mono text-muted-foreground tracking-widest uppercase">Maximum Effort</span>
              <span className="font-bold text-base gold-gradient-text tracking-wide">DJ E<span className="text-xl">X</span>Xtra</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <a
                key={`nav-${link?.label}`}
                href={link?.href}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200 hover:text-glow-gold"
              >
                {link?.label}
              </a>
            ))}
            <Link
              href="/white-label"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-accent transition-colors duration-200"
            >
              White Label
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-accent/20 text-accent border border-accent/30 leading-none">NEW</span>
            </Link>
          </div>

          {/* Portal buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/client-dashboard"
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition-all duration-200"
            >
              Client Dashboard
            </Link>
            <Link
              href="/sign-up-login-screen?role=vendor"
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-accent/40 text-accent hover:bg-accent/10 transition-all duration-200"
            >
              Vendor Portal
            </Link>
            <Link
              href="/sign-up-login-screen?role=admin"
              className="px-4 py-2 text-sm font-semibold rounded-lg gold-gradient text-black hover:opacity-90 transition-all duration-200 active:scale-95"
            >
              Admin Login
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-card/98 backdrop-blur-md border-b border-border px-6 py-4 flex flex-col gap-4">
            {navLinks?.map((link) => (
              <a
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 pt-2 border-t border-border">
              <Link href="/client-dashboard" className="text-center px-4 py-2 text-sm font-semibold rounded-lg border border-primary/40 text-primary">Client Dashboard</Link>
              <Link href="/sign-up-login-screen?role=vendor" className="text-center px-4 py-2 text-sm font-semibold rounded-lg border border-accent/40 text-accent">Vendor Portal</Link>
              <Link href="/sign-up-login-screen?role=admin" className="text-center px-4 py-2 text-sm font-semibold rounded-lg gold-gradient text-black">Admin Login</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}